import { FormBlockInstance } from '../api-types';

export const TEXT_INPUT_TYPE = 'text_input';
export const TEXT_INPUT_TYPE_VERSION = 1;

/** Mirrors the server's default, so a thin config never leaves the field unbounded. */
export const DEFAULT_MAX_LENGTH = 120;

/**
 * The formats this version of pro-core offers.
 *
 * Deliberately short. Identifier formats (IBAN, tax id) are withheld server-side until answers can
 * be stored without becoming freely queryable, and a renderer that offered them would be describing
 * a field the server refuses to author.
 */
export type TextInputFormat = 'PLAIN' | 'DIGITS' | 'MATRICULATION';

export interface TextInputConfig {
  format: TextInputFormat;
  minLength: number | null;
  maxLength: number;
}

export interface TextInputAnswer {
  text: string;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asPositiveInt = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : null;

const asFormat = (value: unknown): TextInputFormat =>
  value === 'DIGITS' || value === 'MATRICULATION' ? value : 'PLAIN';

export const readTextInputConfig = (
  block: FormBlockInstance
): TextInputConfig => {
  const interaction = asRecord(asRecord(block.config).interaction);
  return {
    format: asFormat(interaction.format),
    minLength: asPositiveInt(interaction.minLength),
    maxLength: asPositiveInt(interaction.maxLength) ?? DEFAULT_MAX_LENGTH
  };
};

export const readTextInputAnswer = (
  answer: unknown
): TextInputAnswer | null => {
  const text = asRecord(answer).text;
  return typeof text === 'string' ? { text } : null;
};

const PATTERNS: Record<TextInputFormat, RegExp | null> = {
  PLAIN: null,
  DIGITS: /^[0-9]+$/,
  MATRICULATION: /^[A-Za-z0-9][A-Za-z0-9-]{0,31}$/
};

/** Mirrors the server's anchored check, so "looks fine" here and "accepted" there agree. */
export const matchesFormat = (
  format: TextInputFormat,
  text: string
): boolean => {
  const pattern = PATTERNS[format];
  return pattern === null || pattern.test(text);
};

/**
 * Whether the answer is complete enough to move on.
 *
 * Blank is absent, matching the server: a required field must not be satisfiable with a space. The
 * format is checked too — Next enabling on a value the submit would refuse is the failure this
 * mirroring exists to prevent.
 */
export const isTextInputAnswered = (
  answer: unknown,
  config: TextInputConfig
): boolean => {
  const text = readTextInputAnswer(answer)?.text.trim() ?? '';
  if (!text) {
    return false;
  }
  if (config.minLength !== null && text.length < config.minLength) {
    return false;
  }
  if (text.length > config.maxLength) {
    return false;
  }
  return matchesFormat(config.format, text);
};
